import { useEffect, useRef } from 'react';
import { useSegments } from 'expo-router';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { showToast } from '@/store/slices/uiSlice';
import { useRedeemReferralCodeMutation } from '@/store/api/referralApi';
import { useTranslation } from '@/i18n';
import { clearPendingReferralCode, getPendingReferralCode } from '@/utils/referralStorage';

/**
 * Applies an invite code that is waiting on the device (captured from a link or from the Play
 * install referrer) as soon as the person is signed in. Covers someone who already had an account
 * and logs in, or opens an invite link later. New sign-ups are handled on the verification screen,
 * so nothing runs while the auth screens are showing.
 *
 * The server decides whether the code still counts (a code works only soon after sign-up and
 * before a first finished booking); a definite "no" clears it so it is not retried forever.
 */
export function useApplyPendingReferral() {
  const dispatch = useAppDispatch();
  const { tText } = useTranslation();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const segments = useSegments();
  const inAuthFlow = (segments as string[])[0] === '(auth)';
  const [redeem] = useRedeemReferralCodeMutation();
  const running = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || inAuthFlow || running.current) return;
    running.current = true;
    void (async () => {
      try {
        const code = await getPendingReferralCode();
        if (!code) return;
        try {
          await redeem({ code }).unwrap();
          await clearPendingReferralCode();
          dispatch(
            showToast({
              type: 'success',
              message: tText('Invite code applied. You will earn loyalty points after your first booking.', 'ইনভাইট কোড যুক্ত হয়েছে। প্রথম বুকিংয়ের পর আপনি লয়্যালটি পয়েন্ট পাবেন।'),
            }),
          );
        } catch (err) {
          const status = (err as { status?: number })?.status;
          // Invalid, own code, too late, or already used: final answers, so stop keeping it.
          if (status === 400 || status === 404 || status === 409) await clearPendingReferralCode();
        }
      } finally {
        running.current = false;
      }
    })();
  }, [isAuthenticated, inAuthFlow, redeem, dispatch, tText]);
}
